import classNames from 'classnames';
import React from 'react';
import type { ContentCardBlockSize } from '../ContentCard/ContentCardBlockSize.csharp';
import { ContentCardBlockVariant } from '../ContentCard/ContentCardBlockVariant.csharp';
import type { ContentCardButtonType } from '../ContentCard/ContentCardButtonType.csharp';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { Link } from '~/Partials/Link/Link';
import { LinkType } from '~/Partials/Link/LinkType.csharp';
import type { LinkViewModel } from '~/Partials/Link/LinkViewModel.csharp';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import styles from './ContentCardLayout.module.css';

export interface ContentCardLayoutProps {
    title: string;
    description: string;
    variant: ContentCardBlockVariant;
    useLegacyLink: boolean;
    buttonType: ContentCardButtonType;
    linkRef: React.RefObject<HTMLAnchorElement>;
    size: ContentCardBlockSize;
    callToActionLink?: LinkViewModel;
    stretched?: boolean;
}

const getButton = (
    buttonType: `${ContentCardButtonType}`,
    variant: ContentCardBlockVariant,
    callToActionLink: LinkViewModel,
    useLegacyLink: boolean,
    ref: React.Ref<HTMLAnchorElement>
) => {
    if (useLegacyLink) {
        return <Link {...callToActionLink} showIsExternal={false} className={styles.Link} ref={ref} />;
    }

    if (buttonType === 'circleButton') {
        return (
            <Link
                {...callToActionLink}
                type={LinkType.Clear}
                showIsExternal={false}
                className={styles.Link}
                ref={ref}
                title={callToActionLink.label}
            >
                <span className="visuallyHidden">{callToActionLink.label}</span>
                <SvgIcon iconName="arrow-right-circle" className={styles.CircleButtonIcon} />
            </Link>
        );
    }

    return (
        <Link
            {...callToActionLink}
            type={LinkType.Button}
            buttonSize={ButtonSize.Large}
            buttonTier={getButtonTier(variant)}
            showIsExternal={false}
            className={styles.Link}
            ref={ref}
        >
            <span>{callToActionLink.label}</span>
            <SvgIcon iconName="arrow-right" />
        </Link>
    );
};

const getButtonTier = (variant: ContentCardBlockVariant) => {
    switch (variant) {
        case ContentCardBlockVariant.LightGreyPrimary:
        case ContentCardBlockVariant.DarkGreyPrimary:
        case ContentCardBlockVariant.Yellow:
        case ContentCardBlockVariant.Blue:
        case ContentCardBlockVariant.LightTransparent:
            return ButtonTier.Primary;
        case ContentCardBlockVariant.LightGreySecondary:
        case ContentCardBlockVariant.DarkGreySecondary:
        case ContentCardBlockVariant.BlackSecondary:
            return ButtonTier.Secondary;
        case ContentCardBlockVariant.BlackPrimary:
        case ContentCardBlockVariant.DarkRed:
        case ContentCardBlockVariant.DarkTransparent:
            return ButtonTier.Tertiary;
        case ContentCardBlockVariant.Legacy:
        default:
            return ButtonTier.LegacyPrimary;
    }
};

export const ContentCardLayout = (props: ContentCardLayoutProps) => {
    const { title, description, variant, callToActionLink, useLegacyLink, buttonType, linkRef, size, stretched } =
        props;

    const hasLink = callToActionLink?.href !== undefined;

    return (
        <div
            className={classNames(
                styles.ContentCardLayout,
                variant === ContentCardBlockVariant.Legacy
                    ? [styles['Variant--legacy'], styles[`Variant--${size}`]]
                    : styles[`Variant--${variant}`],
                stretched && styles['Variant--stretched']
            )}
        >
            <h2 className={styles.Title}>{title}</h2>
            <div className={styles.Description}>{description}</div>
            {hasLink && getButton(buttonType, variant, callToActionLink, useLegacyLink, linkRef)}
        </div>
    );
};
